import { useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Fingerprint, RefreshCw, Power, Clock, Trash2, DoorOpen, Users, Settings,
  BellOff, RotateCcw, Wifi, WifiOff, AlertTriangle, Copy, CheckCheck, Server,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useDevices, useDeviceActions } from '../hooks/useBiometricAdmin';
import { relativeTime, istDateTime, type BiometricDevice } from '../biometricTypes';
import { DeviceRegisterModal } from './DeviceRegisterModal';

/** Actions that erase data or interrupt the device need the word typed out. */
interface PendingAction {
  device: BiometricDevice;
  action: string;
  label: string;
  confirmWord: string;
  warning: string;
}

/** Read-only banner — shows the ADMS endpoint to configure on the eSSL device. */
function ConnectionSetupBanner() {
  const [copied, setCopied] = useState(false);
  const { data } = useQuery({
    queryKey: ['biometric-connection-info'],
    queryFn: async () => (await api.get<{
      adms_url: string; device_count: number; online_count: number; protocol_note: string; port_note: string;
    }>('/api/biometric/v2/connection-info')).data,
    staleTime: 60_000,
  });

  const url = data?.adms_url ?? '…';

  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <Server className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm font-semibold text-on-surface">
            eSSL Device Connection Setup
          </p>
          <p className="text-xs text-on-surface-variant">
            Go to your eSSL device → <strong>Comm → Cloud Server</strong> and enter this URL:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-surface-dim px-3 py-1.5 font-mono text-xs text-primary border border-outline-variant">
              {url}
            </code>
            <button
              type="button"
              onClick={copy}
              className="flex items-center gap-1 rounded-md border border-outline-variant px-2 py-1.5 text-xs text-on-surface-variant hover:bg-surface-variant transition-colors"
            >
              {copied ? <CheckCheck className="h-3.5 w-3.5 text-success-text" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          {data?.port_note && (
            <p className="text-xs text-warning-text">{data.port_note}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-on-surface-variant">
            <span>
              <span className="font-medium text-on-surface">{data?.device_count ?? '—'}</span> registered
            </span>
            <span>·</span>
            <span>
              <span className={`font-medium ${(data?.online_count ?? 0) > 0 ? 'text-success-text' : 'text-on-surface'}`}>
                {data?.online_count ?? '—'}
              </span> online now
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DeviceList() {
  const { data: devices, isLoading } = useDevices();
  const { command, deactivate } = useDeviceActions();
  const [registering, setRegistering] = useState(false);
  const [editing, setEditing] = useState<BiometricDevice | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [typed, setTyped] = useState('');

  const run = async (device: BiometricDevice, action: string, label: string, body?: unknown) => {
    try {
      await command.mutateAsync({ sn: device.serial_number, action, body });
      toast.success(`${label} queued for ${device.serial_number}. The device will pick it up on its next poll.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const confirmAndRun = async () => {
    if (!pending) return;
    if (typed !== pending.confirmWord) {
      toast.error(`Type ${pending.confirmWord} exactly to confirm.`);
      return;
    }
    await run(pending.device, pending.action, pending.label, { confirm: pending.confirmWord });
    setPending(null);
    setTyped('');
  };

  const ask = (device: BiometricDevice, action: string, label: string, confirmWord: string, warning: string) => {
    setTyped('');
    setPending({ device, action, label, confirmWord, warning });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Connection setup banner — read-only, shows ADMS URL for device config */}
      <ConnectionSetupBanner />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">Devices</h2>
          <p className="text-sm text-on-surface-variant">
            Machines that push to this server over the internet. No office PC required.
          </p>
        </div>
        <Button onClick={() => setRegistering(true)}>
          <Fingerprint className="mr-2 h-4 w-4" />
          Add device
        </Button>
      </div>

      {!devices?.length ? (
        <EmptyState
          title="No devices yet"
          description="Point a device at this server from its Comm → Cloud Server menu, then approve it here."
          action={<Button onClick={() => setRegistering(true)}>Add device</Button>}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {devices.map(device => (
            <Card key={device._id} className="space-y-4 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
                        device.online ? 'bg-success' : 'bg-danger'
                      }`}
                      aria-hidden
                    />
                    <h3 className="truncate font-semibold text-on-surface">
                      {device.name || device.serial_number}
                    </h3>
                  </div>
                  <p className="mt-0.5 truncate font-mono text-xs text-on-surface-variant">
                    SN {device.serial_number}
                    {device.location ? ` · ${device.location}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Badge tone={device.online ? 'success' : 'danger'}>
                    {device.online ? <Wifi className="mr-1 h-3 w-3" /> : <WifiOff className="mr-1 h-3 w-3" />}
                    {device.online ? 'Online' : 'Offline'}
                  </Badge>
                  {!device.is_active && <Badge tone="warning">Pending approval</Badge>}
                </div>
              </div>

              {!device.is_active && (
                <div className="flex items-start gap-2 rounded-md bg-warning/10 p-2 text-xs text-warning-text">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    This device announced itself but is not approved, so its data is rejected.
                    Open Edit and switch it on once you recognise the serial number.
                  </span>
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <Stat label="Firmware" value={device.firmware_version || '—'} />
                <Stat label="Users" value={device.user_count ?? 0} />
                <Stat label="Fingerprints" value={device.fp_count ?? 0} />
                <Stat label="Faces" value={device.face_count ?? 0} />
                <Stat label="Punches" value={device.total_punches_received ?? 0} />
                <Stat label="Queue" value={device.pending_commands ?? 0} />
                <Stat label="Heartbeat" value={relativeTime(device.last_heartbeat_at)} />
                <Stat label="Last punch" value={relativeTime(device.last_punch_at)} />
              </dl>

              <div className="flex flex-wrap gap-1.5 border-t border-outline-variant pt-3">
                <Action icon={RefreshCw} label="Ping" onClick={() => run(device, 'ping', 'Device info')} />
                <Action icon={Clock} label="Sync time" onClick={() => run(device, 'sync-time', 'Clock sync')} />
                <Action icon={RotateCcw} label="Resync" onClick={() => run(device, 'resync', 'Full resync')} />
                <Action icon={DoorOpen} label="Open door" onClick={() => run(device, 'open-door', 'Door unlock')} />
                <Action icon={BellOff} label="Cancel alarm" onClick={() => run(device, 'cancel-alarm', 'Alarm cancel')} />
                <Action
                  icon={Users}
                  label="Restore users"
                  onClick={() => run(device, 'restore-users', 'User + template restore')}
                />
                <Action icon={Settings} label="Edit" onClick={() => setEditing(device)} />
                <Action
                  icon={Power}
                  label="Reboot"
                  tone="danger"
                  onClick={() => ask(device, 'reboot', 'Reboot', 'REBOOT', 'The device will restart and be offline for about a minute.')}
                />
                <Action
                  icon={Trash2}
                  label="Clear log"
                  tone="danger"
                  onClick={() =>
                    ask(device, 'clear-log', 'Clear attendance log', 'CLEAR',
                      'Erases the punch log stored on the device. Punches already uploaded to Zynx are kept.')
                  }
                />
                <Action
                  icon={Trash2}
                  label="Factory erase"
                  tone="danger"
                  onClick={() =>
                    ask(device, 'clear-data', 'Erase all device data', 'ERASE',
                      'Erases every user, fingerprint and face on the device. Only "Restore users" can bring them back.')
                  }
                />
              </div>

              <button
                type="button"
                className="text-xs text-danger-text hover:underline"
                onClick={async () => {
                  if (!window.confirm(`Deactivate ${device.serial_number}? Its history is kept.`)) return;
                  try {
                    await deactivate.mutateAsync(device.serial_number);
                    toast.success('Device deactivated.');
                  } catch (err) {
                    toast.error(getApiErrorMessage(err));
                  }
                }}
              >
                Deactivate device
              </button>
            </Card>
          ))}
        </div>
      )}

      {(registering || editing) && (
        <DeviceRegisterModal
          device={editing}
          onClose={() => {
            setRegistering(false);
            setEditing(null);
          }}
        />
      )}

      <Modal
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        title={pending ? `${pending.label} — ${pending.device.serial_number}` : ''}
      >
        {pending && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md bg-danger/10 p-3 text-sm text-danger-text">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{pending.warning}</span>
            </div>
            <div>
              <label className="mb-1 block text-sm text-on-surface-variant">
                Type <span className="font-mono font-semibold">{pending.confirmWord}</span> to confirm
              </label>
              <Input value={typed} onChange={e => setTyped(e.target.value)} placeholder={pending.confirmWord} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPending(null)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={typed !== pending.confirmWord}
                onClick={confirmAndRun}
              >
                {pending.label}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {devices?.some(d => !d.online && d.is_active) && (
        <p className="text-xs text-on-surface-variant">
          Offline means no heartbeat within twice the device&apos;s poll interval. Check the device&apos;s
          network settings and that its Cloud Server address still points here.
          Last seen times: {devices.filter(d => !d.online && d.is_active).map(d => `${d.serial_number} ${istDateTime(d.last_heartbeat_at)}`).join(', ')}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <>
      <dt className="text-on-surface-variant">{label}</dt>
      <dd className="text-right font-medium text-on-surface">{value}</dd>
    </>
  );
}

function Action({
  icon: Icon,
  label,
  onClick,
  tone,
}: {
  icon: typeof RefreshCw;
  label: string;
  onClick: () => void;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
        tone === 'danger'
          ? 'border-danger/30 text-danger-text hover:bg-danger/10'
          : 'border-outline-variant text-on-surface-variant hover:bg-surface-variant'
      }`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </button>
  );
}
